import * as React from "react";
import { format, subDays } from "date-fns";
import { cn, formatDate } from "../../../utils/helpers";
import { IAppCustomDates } from "../../../utils/types";
import {
    AccommodationNoteCategory,
    ConsentStatus,
    IConsentEvidence,
    IConsentReport,
} from "../types/reports.types";

export const getAccommodationNoteCategoryLabel = (
    category: AccommodationNoteCategory
) => {
    if (category === AccommodationNoteCategory.THERAPY) {
        return "Therapy";
    }
    if (category === AccommodationNoteCategory.TESTING) {
        return "Psych";
    }
    return "Med";
};

export const AGING_AGE_FILTERS = new Map<string, number>([
    ["30_days_and_older", 30],
    ["60_days_and_older", 60],
    ["90_days_and_older", 90],
    ["180_days_and_older", 180],
]);

export const getAgingReportDateFilters = (
    customDate: string,
    fromDate: string | null,
    toDate: string | null,
    today = new Date()
) => {
    const minAgeDays = AGING_AGE_FILTERS.get(customDate);

    if (minAgeDays === undefined) {
        return {
            custom_date: customDate,
            from_date: fromDate,
            to_date: toDate,
        };
    }

    return {
        custom_date: IAppCustomDates.ALL_TIME,
        from_date: fromDate,
        to_date: toDate ?? format(subDays(today, minAgeDays), "yyyy-MM-dd"),
    };
};

export const formatConsentTimestamp = (value?: string | null) => {
    if (!value) return "Not recorded";
    return formatDate(value, false, "MM/dd/yyyy h:mmaaa");
};

export const getConsentMetadataString = (
    metadata: Record<string, unknown> | undefined,
    key: string
) => {
    const value = metadata?.[key];
    return typeof value === "string" && value.trim() ? value : undefined;
};

export const formatConsentAuthenticationMethod = (value?: string) => {
    if (value === "consent_link") return "Intake consent link";
    return "Patient portal login";
};

export const describeConsentDevice = (userAgent?: string) => {
    if (!userAgent) return "Not recorded";

    let deviceType = "Unknown device";
    if (/iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent)) {
        deviceType = "Tablet";
    } else if (/iPhone|iPod|Mobile|Android/i.test(userAgent)) {
        deviceType = "Mobile";
    } else if (/Windows|Macintosh|Mac OS X|Linux|CrOS/i.test(userAgent)) {
        deviceType = "Computer";
    }

    const edgeVersion = userAgent.match(/Edg(?:A|iOS)?\/(\d+)/)?.[1];
    const chromeVersion = userAgent.match(/(?:Chrome|CriOS)\/(\d+)/)?.[1];
    const firefoxVersion = userAgent.match(/(?:Firefox|FxiOS)\/(\d+)/)?.[1];
    const safariVersion = userAgent.match(/Version\/(\d+).+Safari/)?.[1];
    let browser = "Unknown browser";
    if (edgeVersion) browser = `Edge ${edgeVersion}`;
    else if (chromeVersion) browser = `Chrome ${chromeVersion}`;
    else if (firefoxVersion) browser = `Firefox ${firefoxVersion}`;
    else if (safariVersion) browser = `Safari ${safariVersion}`;

    let operatingSystem = "Unknown OS";
    if (/iPhone|iPad|iPod/i.test(userAgent)) operatingSystem = "iOS";
    else if (/Android/i.test(userAgent)) operatingSystem = "Android";
    else if (/Windows/i.test(userAgent)) operatingSystem = "Windows";
    else if (/CrOS/i.test(userAgent)) operatingSystem = "ChromeOS";
    else if (/Mac OS X|Macintosh/i.test(userAgent)) operatingSystem = "macOS";
    else if (/Linux/i.test(userAgent)) operatingSystem = "Linux";

    return `${deviceType} · ${browser} · ${operatingSystem}`;
};

export const getOrderedPageEvidence = (
    evidences: IConsentReport["evidences"],
    pageCount: number,
    paymentCardAddedAt?: string | null
) => {
    const numericEvidence = (evidences ?? [])
        .filter(
            (evidence) =>
                evidence.page_number === undefined &&
                /^\d+$/.test(evidence.step)
        )
        .sort(
            (first, second) =>
                new Date(first.timestamp).getTime() -
                new Date(second.timestamp).getTime()
        );
    if (numericEvidence.length === 0) {
        const openedEvidence = evidences?.find(
            (evidence) => evidence.step === "client_opened"
        );
        return openedEvidence ? [openedEvidence] : [];
    }

    const cardBoundaryIndex = numericEvidence.findIndex(
        (evidence, index) =>
            index > 0 &&
            evidence.step === numericEvidence[index - 1].step &&
            numericEvidence
                .slice(index + 1)
                .some(
                    (laterEvidence) =>
                        Number(laterEvidence.step) > Number(evidence.step)
                )
    );
    const orderedEvidence = numericEvidence;
    if (cardBoundaryIndex >= 0 && pageCount > numericEvidence.length) {
        const finalPageEvidence = numericEvidence[numericEvidence.length - 1];
        orderedEvidence.splice(
            cardBoundaryIndex + 1,
            0,
            paymentCardAddedAt
                ? { ...finalPageEvidence, timestamp: paymentCardAddedAt }
                : finalPageEvidence
        );
    }

    const reversedEvidences: IConsentEvidence[] = evidences
        ? [...evidences].reverse()
        : [];
    return Array.from(
        { length: Math.max(pageCount, orderedEvidence.length) },
        (_, index) =>
            reversedEvidences.find(
                (evidence) => evidence.page_number === index + 1
            ) ?? orderedEvidence[index]
    );
};

export function getConsentStatusDisplay(status: ConsentStatus): string {
    const statusMap = {
        [ConsentStatus.NOT_SENT]: "Not Sent",
        [ConsentStatus.SENT]: "Sent",
        [ConsentStatus.RECEIVED]: "Received",
        [ConsentStatus.NEEDS_CORRECTION]: "Needs Correction",
        [ConsentStatus.COMPLETED]: "Completed",
        [ConsentStatus.CANCELLED]: "Cancelled",
        [ConsentStatus.PAUSED]: "Paused",
        [ConsentStatus.NEEDS_RENEWAL]: "Needs Renewal",
    };
    return statusMap[status] || status;
}

export function getConsentStatusTag(status: ConsentStatus): string {
    const tagMap = {
        [ConsentStatus.NOT_SENT]:
            "bg-[--color-danger-bg] text-[--color-danger-text]",
        [ConsentStatus.SENT]: "bg-[--color-info-bg] text-[--color-info-text]",
        [ConsentStatus.RECEIVED]:
            "bg-[--color-info-bg] text-[--color-info-text]",
        [ConsentStatus.CANCELLED]:
            "bg-[--color-danger-bg] text-[--color-danger-text]",
        [ConsentStatus.NEEDS_CORRECTION]:
            "bg-[--color-warning-bg] text-[--color-warning-text]",
        [ConsentStatus.PAUSED]:
            "bg-[--color-warning-bg] text-[--color-warning-text]",
        [ConsentStatus.NEEDS_RENEWAL]:
            "bg-[--color-warning-bg] text-[--color-warning-text]",
        [ConsentStatus.COMPLETED]:
            "bg-[--color-success-bg] text-[--color-success-text]",
    };
    return tagMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
}

export function ConsentStatusBadge({
    status,
    className,
}: {
    status: ConsentStatus;
    className?: string;
}) {
    return (
        <span
            className={cn(
                "capitalize text-xs font-medium px-2 py-1 rounded",
                getConsentStatusTag(status),
                className
            )}
        >
            {getConsentStatusDisplay(status)}
        </span>
    );
}

export const getProvidersActivityNavigateUrl = () => {
    const filtersFromLS = localStorage.getItem("providersactivity_filters");

    if (filtersFromLS) {
        return `/reports/provider-session-activity?${filtersFromLS}`;
    }
    return `/reports/provider-session-activity`;
};

export const getAgingNavigateUrl = () => {
    const filtersFromLS = localStorage.getItem("agingreports_filters");

    if (filtersFromLS) {
        return `/reports/aging-reports?${filtersFromLS}`;
    }
    return `/reports/aging-reports`;
};

export const getConsentReportsNavigateUrl = () => {
    const filtersFromLS = localStorage.getItem("consentreports_filters");

    if (filtersFromLS) {
        return `/reports/consent-reports?${filtersFromLS}`;
    }
    return `/reports/consent-reports`;
};
